/**
 * Feedback page — lightweight contact form so users can tell us what
 * could be better. We deliberately keep the field set minimal:
 *   - category radio (bug / idea / praise / other)
 *   - title (3..140)
 *   - body (10..4000)
 *   - optional star rating 1..5
 * The current page URL travels with the submission so we know where
 * the user was when they wrote it.
 */
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Send, Bug, Lightbulb, Heart, MessageSquare, Star, Loader2, CheckCircle2 } from 'lucide-react';
import Layout from '@/components/Layout';
import BackToDashboard from '@/components/BackToDashboard';
import { useLang } from '@/context/LangContext';
import { api } from '@/lib/api';

const CATEGORIES = [
  { id: 'bug',    icon: Bug,           color: 'red',     labelKey: 'fb_cat_bug',    descKey: 'fb_cat_bug_desc' },
  { id: 'idea',   icon: Lightbulb,     color: 'amber',   labelKey: 'fb_cat_idea',   descKey: 'fb_cat_idea_desc' },
  { id: 'praise', icon: Heart,         color: 'emerald', labelKey: 'fb_cat_praise', descKey: 'fb_cat_praise_desc' },
  { id: 'other',  icon: MessageSquare, color: 'sky',     labelKey: 'fb_cat_other',  descKey: 'fb_cat_other_desc' },
];

const COLOR_BORDER = { red: 'border-red-500', amber: 'border-amber-400', emerald: 'border-emerald-400', sky: 'border-sky-400' };
const COLOR_TEXT   = { red: 'text-red-300',    amber: 'text-amber-300',   emerald: 'text-emerald-300', sky: 'text-sky-300' };

export default function Feedback() {
  const { t } = useLang();
  const { pathname } = useLocation();
  const [category, setCategory] = useState('idea');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [rating, setRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (title.trim().length < 3 || body.trim().length < 10) {
      toast.error(t('fb_validation'));
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/feedback', {
        category,
        title: title.trim(),
        body: body.trim(),
        rating: rating || null,
        page: pathname,
      });
      setSubmitted(true);
      setTitle('');
      setBody('');
      setRating(0);
      setCategory('idea');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('fb_send_fail'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <BackToDashboard />
        <header className="mb-6">
          <div className="text-[11px] font-mono uppercase tracking-[0.32em] text-gold/80">{t('fb_eyebrow')}</div>
          <h1 className="text-3xl sm:text-4xl font-h text-white mt-1">{t('fb_title')}</h1>
          <p className="text-sm text-slate-400 mt-1">{t('fb_subtitle')}</p>
        </header>

        {submitted && (
          <div data-testid="fb-success" className="mb-5 rounded-xl border-2 border-emerald-500/40 bg-emerald-500/10 p-4 flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0" />
            <div>
              <div className="text-sm text-emerald-200 font-semibold">{t('fb_success_title')}</div>
              <div className="text-xs text-emerald-300/80">{t('fb_success_body')}</div>
            </div>
            <button
              data-testid="fb-send-another"
              onClick={() => setSubmitted(false)}
              className="ml-auto px-3 py-1.5 rounded-md bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-mono uppercase tracking-widest"
            >
              {t('fb_send_another')}
            </button>
          </div>
        )}

        {!submitted && (
          <form data-testid="fb-form" onSubmit={submit} className="space-y-5">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold/80 mb-2">{t('fb_category')}</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {CATEGORIES.map((c) => {
                  const Icon = c.icon;
                  const active = category === c.id;
                  return (
                    <button
                      type="button"
                      key={c.id}
                      data-testid={`fb-cat-${c.id}`}
                      onClick={() => setCategory(c.id)}
                      className={`rounded-lg border-2 bg-black/30 p-3 text-left transition ${active ? `${COLOR_BORDER[c.color]} bg-black/50` : 'border-white/10 hover:border-white/30'}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className={`w-4 h-4 ${active ? COLOR_TEXT[c.color] : 'text-slate-300'}`} />
                        <div className="text-xs font-mono uppercase tracking-widest text-white">{t(c.labelKey)}</div>
                      </div>
                      <div className="text-[11px] text-slate-400 leading-snug">{t(c.descKey)}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold/80 mb-1 block">{t('fb_field_title')}</label>
              <input
                data-testid="fb-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={140}
                placeholder={t('fb_title_placeholder')}
                className="w-full bg-black/40 border border-white/10 focus:border-gold/40 rounded-md px-3 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none"
              />
              <div className="text-[10px] text-slate-500 mt-1 text-right">{title.length}/140</div>
            </div>

            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold/80 mb-1 block">{t('fb_field_body')}</label>
              <textarea
                data-testid="fb-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={4000}
                rows={6}
                placeholder={t('fb_body_placeholder')}
                className="w-full bg-black/40 border border-white/10 focus:border-gold/40 rounded-md px-3 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none resize-y"
              />
              <div className="text-[10px] text-slate-500 mt-1 text-right">{body.length}/4000</div>
            </div>

            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold/80 mb-2 block">{t('fb_field_rating')}</label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    type="button"
                    key={n}
                    data-testid={`fb-rate-${n}`}
                    onClick={() => setRating(n === rating ? 0 : n)}
                    className="p-1 hover:scale-110 transition"
                    aria-label={`Rate ${n}`}
                  >
                    <Star className={`w-7 h-7 ${n <= rating ? 'fill-gold text-gold' : 'text-slate-600'}`} />
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="submit"
                data-testid="fb-submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-gold text-navy text-sm font-bold uppercase tracking-widest hover:brightness-110 disabled:opacity-60"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submitting ? t('fb_sending') : t('fb_send')}
              </button>
            </div>
          </form>
        )}

        <div className="mt-10 text-[10px] text-slate-500 italic text-center">{t('fb_disclaimer')}</div>
      </div>
    </Layout>
  );
}
